import { useState, useCallback, useEffect } from 'react'
import { router } from '@inertiajs/react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import ThreadList from '@/components/inbox/ThreadList'
import ThreadDetail from '@/components/inbox/ThreadDetail'
import ContextSidebar from '@/components/inbox/ContextSidebar'
import BookingDrawer from '@/components/inbox/BookingDrawer'
import LinkShipmentModal from '@/components/inbox/LinkShipmentModal'
import CreateShipmentModal from '@/components/shipments/CreateShipmentModal'
import ContactPromotionModal, { type ClientOption, type PromotionContact } from '@/components/clients/ContactPromotionModal'
import { useOrgPath } from '@/lib/pathFor'
import { toast } from 'sonner'

export interface SenderInfo {
  email: string | null
  name: string | null
  contact_id: string | null
  contact_role: string | null
  client_id: string | null
  client_name: string | null
  status: 'known_client' | 'unknown_sender' | 'carrier' | 'internal' | 'unmatched'
  latest_email_date: string | null
}

// ThreadItem for the list view
export interface ThreadItem {
  id: string
  thread_hash: string
  subject: string
  participants: string[]
  latest_date: string | null
  emails_count: number
  latest_email_id: string | null
  shipments: Array<{
    id: string
    thread_shipment_id: string
    internal_reference: string
    origin_port: string | null
    destination_port: string | null
    status: string
  }>
  client_name: string | null
  primary_sender?: SenderInfo | null
  has_unknown_sender?: boolean
  review_reasons: string[]
  references: Array<{ type: string; value: string }>
}

// BookingCompact for thread detail
interface BookingCompact {
  id: string
  internal_reference: string
  carrier_booking_id: string | null
  carrier: string | null
  status: string | null
  client_id: string | null
  container_type: string | null
  container_count: number | null
  sailing_date: string | null
}

// Shipment detail for thread view
interface ShipmentDetail {
  id: string
  thread_shipment_id: string
  internal_reference: string
  status: string
  origin_port: string | null
  destination_port: string | null
  client_name: string | null
  bookings: BookingCompact[]
  total_containers: number
  earliest_sailing: string | null
}

// Timeline entry for thread emails
interface TimelineEntry {
  id: string
  type: 'inbound' | 'outbound'
  direction: 'inbound' | 'outbound'
  from: string
  to: string | null
  cc?: string | null
  bcc?: string | null
  date: string | null
  timestamp: string | null
  subject: string | null
  body: string | null
  body_html?: string | null
  summary: string | null
  status: string
  reply_to_email_id?: string | null
  attachments?: Array<{ id: string; filename: string; mime_type: string; size: number }>
  // Outbound-specific fields (present when type === 'outbound')
  original_body?: string | null
  created_at?: string
  created_by_id?: string | null
  sent_at?: string | null
  sent_by_id?: string | null
  agent_session_id?: string | null
  send_error?: string | null
}

// Agent session for thread view
interface AgentSessionDetail {
  id: string
  status: string
  escalation_reason: string | null
  started_at: string | null
  completed_at: string | null
  tool_calls?: Array<{ tool_name: string; arguments: Record<string, unknown>; result: unknown }>
}

// Suggested shipment for thread view
interface SuggestedShipment {
  id: string
  internal_reference: string
  origin_port: string | null
  destination_port: string | null
  client_name: string | null
  matched_carrier_booking_ids: string[]
  matched_internal_booking_refs: string[]
  matched_internal_shipment_refs: string[]
  already_linked: boolean
  suppressed: boolean
}

// ThreadDetail for the show view
export interface ThreadDetail {
  id: string
  thread_hash: string
  subject: string | null
  status: string
  latest_date: string | null
  emails_count: number
  participants: string[]
  senders: SenderInfo[]
  timeline: TimelineEntry[]
  shipments: ShipmentDetail[]
  latest_agent_session: AgentSessionDetail | null
  suggested_shipments: SuggestedShipment[]
  review_reasons: string[]
}

interface ThreadCounts {
  open: number
  pending: number
  closed: number
}

interface InboxIndexProps {
  threads: ThreadItem[]
  filter: string
  attention?: string | null
  counts: ThreadCounts
  search: string
  next_cursor: string | null
  current_cursor: string | null
  selected_thread?: ThreadDetail
  clients?: ClientOption[]
}

export default function InboxIndex({ threads, filter, attention = null, counts, selected_thread, search, next_cursor, current_cursor, clients }: InboxIndexProps) {
  const [bookingDrawerState, setBookingDrawerState] = useState<{ bookingId: string | null; contextKey: string | null }>({
    bookingId: null,
    contextKey: null,
  })
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [promotionContact, setPromotionContact] = useState<PromotionContact | null>(null)
  const [unlinkConfirm, setUnlinkConfirm] = useState<string | null>(null)
  const [createShipmentOpen, setCreateShipmentOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const pathFor = useOrgPath()

  const currentThreadKey = selected_thread ? `${selected_thread.id}:${selected_thread.shipments.map(s => s.id).join(',')}` : null
  const selectedBookingId = bookingDrawerState.contextKey === currentThreadKey ? bookingDrawerState.bookingId : null

  const refreshInbox = useCallback((showFeedback = false) => {
    if (showFeedback) setIsRefreshing(true)

    router.reload({
      preserveScroll: true,
      only: ['threads', 'filter', 'attention', 'counts', 'next_cursor', 'current_cursor', 'selected_thread'],
      onFinish: () => {
        if (showFeedback) setIsRefreshing(false)
      },
    })
  }, [])

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return

      refreshInbox()
    }, 30_000)

    return () => window.clearInterval(intervalId)
  }, [refreshInbox])

  // Callback that resets drawer and opens booking
  const handleOpenBooking = useCallback((bookingId: string) => {
    setBookingDrawerState({ bookingId, contextKey: currentThreadKey })
  }, [currentThreadKey])

  // Callbacks that reset the drawer when modals close
  const handleCloseAssignModal = useCallback(() => {
    setBookingDrawerState({ bookingId: null, contextKey: null })
    setAssignModalOpen(false)
  }, [])

  const handleCloseCreateModal = useCallback(() => {
    setBookingDrawerState({ bookingId: null, contextKey: null })
    setCreateShipmentOpen(false)
  }, [])

  async function handleAssignSuggestedShipment(shipmentId: string) {
    if (!selected_thread?.id) return

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const resp = await fetch(pathFor('/thread_shipments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ shipment_id: shipmentId, email_thread_id: selected_thread.id }),
      })

      if (resp.ok) {
        toast.success('Thread assigned to shipment')
        router.reload({ preserveScroll: true })
      } else {
        const data = await resp.json().catch(() => ({}))
        toast.error(data.error || 'Failed to assign thread')
      }
    } catch {
      toast.error('Failed to assign thread')
    }
  }

  function handleSuppressShipment(threadShipmentId: string) {
    setUnlinkConfirm(threadShipmentId)
  }

  function confirmUnlinkShipment() {
    if (!unlinkConfirm) return

    router.patch(pathFor(`/thread_shipments/${unlinkConfirm}/suppress`), {}, {
      preserveState: true,
      onSuccess: () => toast.success('Shipment unlinked from thread'),
      onError: () => toast.error('Failed to unlink shipment'),
    })
    setUnlinkConfirm(null)
  }

  async function handleUpdateThreadStatus(newStatus: string) {
    if (!selected_thread?.id) return

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const resp = await fetch(pathFor(`/inbox/threads/${selected_thread.id}/status`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ status: newStatus }),
      })

      if (resp.ok) {
        toast.success(`Thread marked as ${newStatus}`)
        const params = new URLSearchParams()
        if (filter) params.set('filter', filter)
        if (attention) params.set('attention', attention)
        const query = params.toString()
        router.visit(pathFor(`/inbox${query ? `?${query}` : ''}`), { preserveScroll: true })
      } else {
        const data = await resp.json().catch(() => ({}))
        toast.error(data.error || 'Failed to update thread status')
      }
    } catch {
      toast.error('Failed to update thread status')
    }
  }

  return (
    <div className="flex h-full">
      {/* Column 2: Thread List */}
      <ThreadList
        threads={threads}
        filter={filter}
        attention={attention}
        counts={counts}
        selectedThreadId={selected_thread?.id}
        search={search}
        next_cursor={next_cursor}
        current_cursor={current_cursor}
        onRefresh={() => refreshInbox(true)}
        isRefreshing={isRefreshing}
      />

      {/* Column 3: Thread Detail */}
      <div className="flex-1 min-w-0">
        {selected_thread ? (
          <ThreadDetail
            thread={selected_thread}
            filter={filter}
            attention={attention}
            search={search}
            cursor={current_cursor}
            onUpdateStatus={handleUpdateThreadStatus}
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-muted-foreground">Select a thread to view</p>
          </div>
        )}
      </div>

      {/* Column 4: Context Sidebar */}
      {selected_thread && (
        <ContextSidebar
          shipments={selected_thread.shipments}
          suggestedShipments={selected_thread.suggested_shipments}
          senders={selected_thread.senders || []}
          agentSession={selected_thread.latest_agent_session}
          reviewReasons={selected_thread.review_reasons}
          onOpenBooking={handleOpenBooking}
          onAssignShipment={() => setAssignModalOpen(true)}
          onAssignSuggestedShipment={handleAssignSuggestedShipment}
          onSuppressShipment={handleSuppressShipment}
          onPromoteSender={(sender) => setPromotionContact({
            id: sender.contact_id,
            name: sender.name,
            email: sender.email || '',
            company: sender.client_name,
          })}
        />
      )}

      {/* Assign to Shipment Modal */}
      <LinkShipmentModal
        open={assignModalOpen}
        onClose={handleCloseAssignModal}
        emailThreadId={selected_thread?.id || null}
        linkedShipmentIds={(selected_thread?.shipments || []).map(s => s.id)}
        onCreateShipment={() => setCreateShipmentOpen(true)}
        filter={filter}
        attention={attention}
        search={search}
        cursor={current_cursor}
      />

      {/* Create Shipment Modal */}
      <CreateShipmentModal
        open={createShipmentOpen}
        onClose={handleCloseCreateModal}
        emailThreadId={selected_thread?.id}
        clients={clients || []}
      />

      {/* Unlink Shipment Confirmation */}
      <AlertDialog open={!!unlinkConfirm} onOpenChange={(open) => !open && setUnlinkConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink Shipment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unlink this shipment from the thread? You can re-link it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmUnlinkShipment} className="bg-red-600 hover:bg-red-700 cursor-pointer">
              Unlink
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Booking Drawer */}
      <BookingDrawer
        linkedBookingsFull={selected_thread?.shipments.flatMap(s => s.bookings.map(b => ({
          ...b,
          shipment_id: s.id,
          origin_port: s.origin_port,
          destination_port: s.destination_port,
        }))) || []}
        selectedBookingId={selectedBookingId}
        onClose={() => setBookingDrawerState({ bookingId: null, contextKey: null })}
      />

      <ContactPromotionModal
        key={promotionContact?.email || 'no-contact'}
        open={!!promotionContact}
        onClose={() => setPromotionContact(null)}
        contact={promotionContact}
        clients={clients || []}
        emailThreadId={selected_thread?.id || null}
        shipments={(selected_thread?.shipments || []).map((shipment) => ({
          id: shipment.id,
          origin_port: shipment.origin_port,
          destination_port: shipment.destination_port,
          client_name: shipment.client_name,
          unassigned_bookings_count: shipment.bookings.filter((booking) => !booking.client_id).length,
        }))}
      />
    </div>
  )
}

InboxIndex.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>{page}</AuthenticatedLayout>
)
